<template>
  <b-tabs
    pills
    class="w-100"
  >
    <b-alert
      variant="primary"
      show
    >Atenção: Os dados abaixo refletem informações em tempo real. Prestações
      de contas ainda não aprovadas podem sofrer alterações.</b-alert>
    <b-tab
      title="Posição"
      active
    >
      <b-card :title="prestacaoConta.mesAno">
        <b-card-text>
          <b-list-group v-if="demonstrativo">
            <b-list-group-item
              v-for="dem in demonstrativo.contas"
              :key="dem.id"
              class="d-flex justify-content-between align-items-center"
            >
              <span class="align-items-left">
                <b-badge
                  pill
                  class="badge-round"
                >
                  {{ dem.conta.codigo }}
                </b-badge>
                {{ dem.conta.nome }}</span>
              <b-badge
                variant="primary"
                pill
                class="badge-round"
              >
                {{ dem.valor | currencyBRL }}
              </b-badge>
            </b-list-group-item>
          </b-list-group>
        </b-card-text>
      </b-card>
    </b-tab>
    <b-tab title="PDF's">
      <b-card :title="prestacaoConta.mesAno">
        <b-card-text>
          <b-list-group v-if="prestacaoConta.arquivos">
            <b-list-group-item
              v-for="arq in prestacaoConta.arquivos"
              :key="arq.arquivo.id"
              class="d-flex justify-content-between align-items-center"
            >
              <span class="align-items-left"> {{ arq.arquivo.nome }}</span>
              <b-button
                icon="download-icon"
                variant="primary"
                type="button"
                :disabled="downloading"
                @click="downloadItem(arq.arquivo)"
              >
                <b-spinner
                  v-if="downloading"
                  small
                />
                <span v-if="!downloading">{{
                  arq.arquivo.tamanho | byteToMegabyte
                }}</span>
              </b-button>
            </b-list-group-item>
          </b-list-group>
        </b-card-text>
      </b-card>
    </b-tab>
    <b-tab
      v-if="podeAssinar"
      title="Aprovações"
    >
      <b-card :title="prestacaoConta.mesAno">
        <b-card-text>
          <!-- Status atual do parecer -->
          <b-alert
            v-if="parecer && parecer.situacao"
            :variant="parecerVariant"
            show
          >
            <strong>Seu parecer:</strong> {{ parecerLabel }}
            <template v-if="parecer.ressalvas">
              <br><em>Ressalvas: {{ parecer.ressalvas }}</em>
            </template>
          </b-alert>

          <b-alert
            v-if="!parecer || !parecer.situacao"
            variant="warning"
            show
          >
            Você ainda não emitiu parecer para esta prestação de contas.
          </b-alert>

          <!-- Signatários -->
          <div
            v-if="prestacaoConta.signatarios && prestacaoConta.signatarios.length"
            class="mb-2"
          >
            <h6>Signatários</h6>
            <b-list-group flush>
              <b-list-group-item
                v-for="sig in prestacaoConta.signatarios"
                :key="sig.id"
                class="d-flex justify-content-between align-items-center"
              >
                <span>{{ sig.nome }}</span>
                <b-badge
                  :variant="signatarioVariant(sig.situacao)"
                  pill
                >
                  {{ signatarioLabel(sig.situacao) }}
                </b-badge>
              </b-list-group-item>
            </b-list-group>
          </div>

          <!-- Formulário de aprovação/rejeição -->
          <div
            v-if="prestacaoConta.situacaoBalancete === 'FECHADO'"
            class="mt-2"
          >
            <h6>Emitir parecer</h6>
            <b-form-group
              label="Ressalvas / Observações"
              label-for="ressalvas"
            >
              <b-form-textarea
                id="ressalvas"
                v-model="ressalvas"
                placeholder="Digite suas ressalvas ou observações..."
                rows="3"
              />
            </b-form-group>

            <div class="d-flex gap-2">
              <b-button
                variant="success"
                :disabled="!!submitting"
                class="mr-1"
                @click="onAprovar"
              >
                <b-spinner
                  v-if="submitting === 'aprovar'"
                  small
                />
                <feather-icon
                  v-if="submitting !== 'aprovar'"
                  icon="CheckCircleIcon"
                  class="mr-50"
                />
                Aprovar
              </b-button>
              <b-button
                variant="danger"
                :disabled="!!submitting || !ressalvas"
                @click="onReprovar"
              >
                <b-spinner
                  v-if="submitting === 'reprovar'"
                  small
                />
                <feather-icon
                  v-if="submitting !== 'reprovar'"
                  icon="XCircleIcon"
                  class="mr-50"
                />
                Reprovar
              </b-button>
            </div>
            <small
              v-if="!ressalvas"
              class="text-muted"
            >Para reprovar, é obrigatório informar as ressalvas.</small>
          </div>

          <b-alert
            v-if="prestacaoConta.situacaoBalancete === 'APROVADO'"
            variant="success"
            show
            class="mt-2"
          >
            Esta prestação de contas já foi aprovada.
          </b-alert>
        </b-card-text>
      </b-card>
    </b-tab>
  </b-tabs>
</template>

<script>
import {
  BListGroup, BListGroupItem, BAlert, BFormGroup, BFormTextarea,
} from 'bootstrap-vue'

export default {
  components: {
    BListGroup, BListGroupItem, BAlert, BFormGroup, BFormTextarea,
  },
  data() {
    return {
      prestacaoConta: {},
      arquivos: [],
      referencia: null,
      downloading: false,
      ressalvas: '',
      submitting: false,
    }
  },
  computed: {
    demonstrativo() {
      return this.$store.state.prestacaocontas.demonstrativo
    },
    parecer() {
      return this.$store.state.prestacaocontas.parecer
    },
    authUser() {
      return this.$store.getters['auth/authUser']()
    },
    podeAssinar() {
      return this.authUser
        && this.authUser.mandato
        && this.authUser.mandato.ativo
        && this.authUser.mandato.assinaPrestacaoContas
    },
    parecerVariant() {
      if (!this.parecer) return 'secondary'
      if (this.parecer.situacao === 'APROVADO') return 'success'
      if (this.parecer.situacao === 'REPROVADO') return 'danger'
      return 'secondary'
    },
    parecerLabel() {
      if (!this.parecer) return ''
      if (this.parecer.situacao === 'APROVADO') return 'Aprovado'
      if (this.parecer.situacao === 'REPROVADO') return 'Reprovado'
      return this.parecer.situacao
    },
  },
  async created() {
    if (this.$route.params.id) {
      this.prestacaoConta = this.$store.getters['prestacaocontas/getOne'](
        Number(this.$route.params.id),
      )
      const ref = this.prestacaoConta.mesAno
      this.referencia = ref.replace('/', '-')
    }

    this.fetchDemonstrativo()

    if (this.podeAssinar) {
      this.fetchParecer()
    }
  },
  methods: {
    async fetchDemonstrativo() {
      if (this.prestacaoConta) {
        await this.$store.dispatch('prestacaocontas/fetchDemonstrativo', {
          referencia: this.referencia,
        })
      }
    },
    async fetchParecer() {
      try {
        await this.$store.dispatch('prestacaocontas/fetchParecer', {
          id: this.prestacaoConta.id,
        })
      } catch (err) {
        // 403 ou sem parecer — ignora silenciosamente
      }
    },
    async onAprovar() {
      this.submitting = 'aprovar'
      try {
        await this.$store.dispatch('prestacaocontas/aprovar', {
          id: this.prestacaoConta.id,
          ressalvas: this.ressalvas || 'Sem ressalvas.',
        })
        this.$toast.success('Prestação de contas aprovada com sucesso')
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.error) || 'Erro ao aprovar'
        this.$toast.error(msg)
      } finally {
        this.submitting = false
      }
    },
    async onReprovar() {
      if (!this.ressalvas) return
      this.submitting = 'reprovar'
      try {
        await this.$store.dispatch('prestacaocontas/reprovar', {
          id: this.prestacaoConta.id,
          ressalvas: this.ressalvas,
        })
        this.$toast.success('Prestação de contas reprovada')
      } catch (err) {
        const msg = (err.response && err.response.data && err.response.data.error) || 'Erro ao reprovar'
        this.$toast.error(msg)
      } finally {
        this.submitting = false
      }
    },
    downloadItem(item) {
      const thiz = this
      thiz.downloading = true
      this.$store
        .dispatch('prestacaocontas/fetchPdf', {
          arquivoId: item.id,
        })
        .then(response => {
          const blob = new Blob([response.data], { type: 'application/pdf' })
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = item.nomeComExtensao
          link.click()
          URL.revokeObjectURL(link.href)
        })
        .finally(() => {
          thiz.downloading = false
        })
    },
    signatarioVariant(situacao) {
      if (situacao === 'APROVADO') return 'success'
      if (situacao === 'REPROVADO') return 'danger'
      return 'warning'
    },
    signatarioLabel(situacao) {
      if (situacao === 'APROVADO') return 'Aprovado'
      if (situacao === 'REPROVADO') return 'Reprovado'
      return 'Pendente'
    },
  },
}
</script>
